import { useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import { paths, userRoles } from 'src/common/config/TextMessage';
import { getAuthenticated, isLocalSeoUser } from 'src/common/config/FunctionHelper';
import { hasLocalSeoCookie } from 'src/common/config/FunctionHelperPartTwo';

// ---------------------------------------------------------------------------
// UserDetails type
// Only the fields this hook reads are declared — all optional because the
// Redux initial state is `{}` (empty object) before the API response arrives.
// ---------------------------------------------------------------------------
interface UserDetails {
  email?: string;
  temp?: boolean;
  role_type?: string;
}

// ---------------------------------------------------------------------------
// Route allow-lists (centralised here so they evolve in one place)
// ---------------------------------------------------------------------------

/** Routes a Local-SEO-only user is allowed to visit. */
const LOCAL_SEO_ALLOWED_ROUTES: string[] = [
  paths.billing,
  paths.settings,
  paths.localChecker,
  paths.googleBusiness,
  paths.mapRankTracker,
  paths.localTasks,
  paths.googleReviews,
  paths.businessPosts,
  paths.listingManager,
  paths.trySeoSpace,
  paths.googleReviewsEmbed,
  paths.googleReviewsWidget
];

/** Keyword-research sub-pages that require a paid (non-free, non-local-seo) plan. */
const KEYWORD_RESEARCH_SUB_PATHS: string[] = [
  paths.blogPostIdeas,
  paths.competitorAnalysis,
  paths.keywordOverview,
  paths.keywordsLists,
  paths.keywordTracking
];

/** Orbit AI sub-pages that require a paid (non-free, non-local-seo) plan. */
const ORBIT_AI_SUB_PATHS: string[] = [
  paths.keywordAssist,
  paths.blogPostAgent,
  paths.contentStrategy,
  paths.contentPlan,
  paths.localSeoAi,
  paths.reviewAutomation
];

/** Local-SEO sub-pages that require at minimum a non-free plan. */
const LOCAL_SEO_SUB_PATHS: string[] = [
  paths.localChecker,
  paths.googleBusiness,
  paths.mapRankTracker,
  paths.localTasks,
  paths.googleReviews,
  paths.businessPosts,
  paths.listingManager,
  paths.citationBuilder,
  paths.googleReviewsWidget,
  paths.googleReviewsEmbed
];

/** Routes accessible even on expired / no-plan accounts (all users). */
const EXPIRE_PLAN_PATHS_DEFAULT: string[] = [
  paths.billing,
  paths.dashboard,
  paths.settings
];

/** Same list narrowed for Local-SEO users (no dashboard access on expire). */
const EXPIRE_PLAN_PATHS_LOCAL_SEO: string[] = [paths.billing, paths.settings];

/** Admin-only routes (blocked for member role). */
const ADMIN_ONLY_ROUTES: string[] = ['/user-management', '/billing'];

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export interface AccessControlResult {
  /** When truthy, the component should redirect to this path. */
  redirectTo: string | null;
  /**
   * True while Stripe data is still loading.
   * The component should render nothing (or a spinner) instead of guessing.
   */
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Encapsulates all route-access-control rules for the application.
 *
 * Returns `{ isLoading, redirectTo }`:
 * - `isLoading === true`  → render nothing while Stripe data loads
 * - `redirectTo !== null` → redirect the user to that path
 * - `redirectTo === null` → allow the route to render
 *
 * @param pathname - `location.pathname` from `useLocation()`
 */
export const useAccessControl = (pathname: string): AccessControlResult => {
  // ── Selectors ────────────────────────────────────────────────────────────
  const subscription = useSelector((state: RootState) => state.user.subscription);
  const isPremiumUser = useSelector((state: RootState) => state.user.premiumUser);
  const stripeLoader = useSelector((state: RootState) => state.siteAudit.stripeLoader) ?? true;
  const siteAuditLoader = useSelector((state: RootState) => state.siteAudit.loader) ?? true;
  const activePlan = useSelector((state: RootState) => state.user.activePlan);
  const planDetails = useSelector((state: RootState) => state.user.planDetails);
  const userDetails = useSelector((state: RootState) => state.counter.userDetails) as UserDetails;

  // ── Derived flags ────────────────────────────────────────────────────────
  const isAuthenticated = getAuthenticated() === 'true';
  const isLocalSeo = isLocalSeoUser() || hasLocalSeoCookie();

  const isFreePlan = activePlan === planDetails?.free;
  const isDiyOrFreePlan =
    activePlan === planDetails?.starterPlus ||
    activePlan === planDetails?.diy ||
    activePlan === planDetails?.free;

  const isOldPlan = subscription?.planVersion === 'v1';
  const isAgency = activePlan === planDetails?.agency;
  const isExpertPlus = activePlan === planDetails?.expertPlus;
  const isEnterprise = activePlan === planDetails?.enterprise;
  const isMember = userDetails?.role_type === userRoles?.member;

  const hasSubscription = subscription?.subscription_id !== undefined;
  const isPausedAccount = Boolean(subscription?.pause_collection?.behavior);

  const loadersSettled = !stripeLoader && !siteAuditLoader;

  const isNoPlanAccount =
    loadersSettled &&
    Boolean(userDetails?.email) &&
    userDetails?.temp === true &&
    (!subscription?.subscription_id || !isPremiumUser);

  const isExpiredAccount =
    loadersSettled &&
    !isFreePlan &&
    Boolean(userDetails?.email) &&
    userDetails?.temp !== true &&
    (!subscription?.subscription_id || !isPremiumUser);

  const isNoPlanOrExpired = isNoPlanAccount || isExpiredAccount;

  const expirePlanPaths = isLocalSeo
    ? EXPIRE_PLAN_PATHS_LOCAL_SEO
    : EXPIRE_PLAN_PATHS_DEFAULT;

  const defaultRedirect = isLocalSeo ? paths.billing : paths.dashboard;

  // ── Guard: still loading Stripe data ─────────────────────────────────────
  if (stripeLoader) {
    return { isLoading: true, redirectTo: null };
  }

  // ── Guard: unauthenticated user on public-only pages (allow through) ──────
  if (!isAuthenticated) {
    const isPublicPassthrough =
      pathname.toLowerCase() === paths.memberSignup || pathname === paths.signup;

    if (isPublicPassthrough) {
      return { isLoading: false, redirectTo: null };
    }

    return { isLoading: false, redirectTo: paths.signin };
  }

  // All guards below this line assume the user IS authenticated.

  // ── Guard: paused subscription ────────────────────────────────────────────
  if (isPausedAccount && !expirePlanPaths.includes(pathname)) {
    return { isLoading: false, redirectTo: defaultRedirect };
  }

  // ── Guard: Local-SEO-only user accessing a disallowed route ──────────────
  if (isLocalSeo && !LOCAL_SEO_ALLOWED_ROUTES.includes(pathname)) {
    const to =
      isNoPlanOrExpired || isPausedAccount ? paths.billing : paths.localChecker;
    return { isLoading: false, redirectTo: to };
  }

  // ── Guard: expired / no-plan account ─────────────────────────────────────
  if (isNoPlanOrExpired && !expirePlanPaths.includes(pathname)) {
    return { isLoading: false, redirectTo: defaultRedirect };
  }

  // ── Guard: already-authenticated user hitting the signup page ─────────────
  if (pathname === paths.signup) {
    return { isLoading: false, redirectTo: paths.dashboard };
  }

  // ── Guard: already-authenticated user hitting member-signup ───────────────
  if (pathname.toLowerCase() === paths.memberSignup) {
    return { isLoading: false, redirectTo: paths.dashboard };
  }

  // ── Guard: keyword research sub-pages require a paid non-local-seo plan ───
  if (KEYWORD_RESEARCH_SUB_PATHS.includes(pathname) && (isFreePlan || isLocalSeo)) {
    return { isLoading: false, redirectTo: paths.keywordResearch };
  }

  // ── Guard: Orbit AI sub-pages require a paid non-local-seo plan ───────────
  if (ORBIT_AI_SUB_PATHS.includes(pathname) && (isFreePlan || isLocalSeo)) {
    return { isLoading: false, redirectTo: paths.orbitAiAgent };
  }

  // ── Guard: Local-SEO sub-pages require at minimum a non-free plan ─────────
  if (LOCAL_SEO_SUB_PATHS.includes(pathname) && isFreePlan) {
    return { isLoading: false, redirectTo: paths.otherTools };
  }

  // ── Guard: admin-only routes are blocked for member-role users ────────────
  if (ADMIN_ONLY_ROUTES.includes(pathname) && isMember) {
    return { isLoading: false, redirectTo: defaultRedirect };
  }

  // ── Guard: user-management requires a non-diy/non-free plan ──────────────
  if (pathname === paths.userManagement && isDiyOrFreePlan) {
    return { isLoading: false, redirectTo: paths.dashboard };
  }

  // ── Guard: lead-generation access depends on plan version ─────────────────
  const isLeadGenerationBlocked = isOldPlan
    ? hasSubscription && !isAgency && !isExpertPlus && !isEnterprise
    : isFreePlan;

  if (pathname === paths.leadGeneration && isLeadGenerationBlocked) {
    return { isLoading: false, redirectTo: paths.dashboard };
  }

  // ── Guard: try-seospace page is only for Local-SEO users ─────────────────
  if (pathname === paths.trySeoSpace && !isLocalSeo) {
    return { isLoading: false, redirectTo: paths.dashboard };
  }

  // ── All guards passed — allow the route ───────────────────────────────────
  return { isLoading: false, redirectTo: null };
};
